import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FireflyConfigService } from '../../../services/firefly-config.service';
import { FireflyService } from '../../../services/firefly.service';
import { ToastService } from '../../../services/toast.service';
import { StyleCategory, StyleLabelMap, StyleImage } from '../../../models/firefly-common.model';
import { I18nService } from '../../../i18n/i18n.service';

export interface StoryboardSettings {
  styleStrength: number;
  structureStrength: number;
  visualIntensity: number;
  stylePresets: string[];
  styleFileId: string | null;
}

@Component({
  selector: 'app-storyboard-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './storyboard-settings.component.html',
  styleUrls: ['./storyboard-settings.component.scss']
})
export class StoryboardSettingsComponent implements OnInit {
  @Input() showSettings: boolean = false;
  @Output() showSettingsChange = new EventEmitter<boolean>();
  @Output() settingsChange = new EventEmitter<StoryboardSettings>();

  styleImage: StyleImage = {
    file: null,
    preview: null,
    fileId: null
  };
  hasStyleImage: boolean = false;

  styleStrength: number = 0;
  structureStrength: number = 0;
  visualIntensity: number = 0;
  stylePresets: string[] = [];

  styleCategories: StyleCategory[] = [];
  styleLabels: StyleLabelMap = {};

  activeStyleCategory = 'popular';
  searchTerm = '';
  isStyleSearchActive = false;

  constructor(
    private fireflyConfigService: FireflyConfigService,
    private fireflyService: FireflyService,
    private toastService: ToastService,
    public i18n: I18nService
  ) {}

  ngOnInit(): void {
    this.styleCategories = this.fireflyConfigService.styleCategories;
    this.styleLabels = this.fireflyConfigService.styleLabels;

    this.styleStrength = this.fireflyConfigService.DEFAULT_STYLE_STRENGTH;
    this.structureStrength = this.fireflyConfigService.DEFAULT_STRUCTURE_STRENGTH;
    this.visualIntensity = this.fireflyConfigService.DEFAULT_VISUAL_INTENSITY;
    this.stylePresets = [...this.fireflyConfigService.DEFAULT_STYLE_PRESETS];

    this.emitSettings();
  }

  toggleSettings(): void {
    this.showSettings = !this.showSettings;
    this.showSettingsChange.emit(this.showSettings);
  }

  onStyleFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (file.type.match('image.*')) {
        if (file.size <= 10 * 1024 * 1024) {
          const reader = new FileReader();

          reader.onload = (e: any) => {
            this.styleImage = {
              file: file,
              preview: e.target.result,
              fileId: null
            };
            this.hasStyleImage = true;

            this.uploadStyleImage(file);
          };

          reader.readAsDataURL(file);
        } else {
          this.toastService.showToast(this.i18n.t('toast.fileTooLarge'), 'error');
        }
      } else {
        this.toastService.showToast(this.i18n.t('toast.unsupportedFormat'), 'error');
      }
    }
  }

  uploadStyleImage(file: File): void {
    this.fireflyService.uploadFile(file).subscribe({
      next: (response) => {
        if (response && response.fileId) {
          this.styleImage.fileId = response.fileId;
          this.emitSettings();
          this.toastService.showToast(this.i18n.t('toast.styleImageUploaded'), 'success');
        } else {
          this.toastService.showToast(this.i18n.t('toast.styleImageUploadFailed'), 'error');
        }
      },
      error: (error) => {
        console.error('スタイル参照画像アップロードエラー:', error);
        this.toastService.showToast(this.i18n.t('toast.styleImageUploadFailed'), 'error');
      }
    });
  }

  removeStyleImage(): void {
    this.styleImage = { file: null, preview: null, fileId: null };
    this.hasStyleImage = false;
    this.emitSettings();
  }

  triggerStyleFileInput(): void {
    document.getElementById('styleImageUpload')?.click();
  }

  toggleStylePreset(preset: string): void {
    const index = this.stylePresets.indexOf(preset);
    if (index > -1) {
      this.stylePresets.splice(index, 1);
    } else {
      this.stylePresets.push(preset);
    }
    this.emitSettings();
  }

  isStyleSelected(preset: string): boolean {
    return this.stylePresets.includes(preset);
  }

  setStyleCategory(categoryId: string): void {
    this.activeStyleCategory = categoryId;
    this.isStyleSearchActive = false;
  }

  searchStyles(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.isStyleSearchActive = input.value.length > 0;
  }

  getStyleLabel(styleId: string): string {
    return this.i18n.t('styleLabel.' + styleId.replace(/-/g, '_'));
  }

  getFilteredStyles(): string[] {
    return this.fireflyConfigService.getFilteredStyles(
      this.searchTerm,
      this.activeStyleCategory,
      this.isStyleSearchActive
    );
  }

  resetToDefaults(): void {
    this.styleStrength = this.fireflyConfigService.DEFAULT_STYLE_STRENGTH;
    this.structureStrength = this.fireflyConfigService.DEFAULT_STRUCTURE_STRENGTH;
    this.visualIntensity = this.fireflyConfigService.DEFAULT_VISUAL_INTENSITY;
    this.stylePresets = [...this.fireflyConfigService.DEFAULT_STYLE_PRESETS];

    if (this.hasStyleImage) {
      this.removeStyleImage();
    }

    this.emitSettings();
    this.toastService.showToast(this.i18n.t('toast.settingsReset'), 'success');
  }

  onSliderChange(): void {
    this.emitSettings();
  }

  private emitSettings(): void {
    this.settingsChange.emit({
      styleStrength: this.styleStrength,
      structureStrength: this.structureStrength,
      visualIntensity: this.visualIntensity,
      stylePresets: this.stylePresets,
      styleFileId: this.styleImage.fileId
    });
  }
}